import { faroCoreApiClient } from "@api/faro-core-api-client";
import {
  AdmCompany,
  AdmProject,
  BaseCompanyIdProps,
} from "@custom-types/base-types";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { isWorkspaceHbToSxgMigrated } from "@utils/plan-utils";

/** Fetch company data */
export const fetchCompany = createAsyncThunk<
  AdmCompany,
  Partial<BaseCompanyIdProps>
>("company/fetchCompany", async ({ companyId }) => {
  if (!companyId) {
    throw new Error("workspaceId is required to fetch workspace data");
  }
  return await faroCoreApiClient.admin.getCompanyById({ companyId });
});

/**
 * Update the selected company type.
 * @param companyId - The ID of the company to update.
 * @param payload - The payload containing the updated information.
 * @returns Some keys of updated company details, such as companyId, companyType, and name.
 */
export const updateCompany = createAsyncThunk<
  FaroCoreApiTypes.UpdateCompanyDetailsResponse,
  FaroCoreApiTypes.UpdateCompanyProps
>("company/updateCompany", async ({ companyId, payload }) => {
  if (!companyId) {
    throw new Error("workspaceId is required to update workspace type");
  }
  return await faroCoreApiClient.admin.updateCompanyDetails({
    companyId,
    payload,
  });
});

/** Fetch the members of a company */
export const fetchCompanyMembers = createAsyncThunk<
  FaroCoreApiTypes.AdmCompanyMemberWithAuth[],
  BaseCompanyIdProps
>("company/fetchCompanyMembers", async ({ companyId }) => {
  return await faroCoreApiClient.admin.getCompanyMembersById({
    companyId,
  });
});

/** Fetch the projects of a company */
export const fetchCompanyProjects = createAsyncThunk<
  AdmProject[],
  BaseCompanyIdProps
>("company/fetchCompanyProjects", async ({ companyId }) => {
  return await faroCoreApiClient.admin.getAllProjectsForCompanyWithId({
    companyId,
  });
});

/** Fetch the groups of a company */
export const fetchCompanyGroups = createAsyncThunk<
  FaroCoreApiTypes.AdmCompanyGroup[],
  BaseCompanyIdProps
>("company/fetchCompanyGroups", async ({ companyId }) => {
  return await faroCoreApiClient.admin.getCompanyGroupsById({
    companyId,
  });
});

/**
 * Fetch whether the workspace has been migrated from HoloBuilder to Sphere XG, derived from the
 * features granted by the workspace plans.
 */
export const fetchCompanyMigrationStatus = createAsyncThunk<
  boolean,
  BaseCompanyIdProps
>("company/fetchCompanyMigrationStatus", async ({ companyId }) => {
  const plans = await faroCoreApiClient.admin.getCompanyPlansById({
    companyId,
  });
  return isWorkspaceHbToSxgMigrated(plans);
});
